import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import { API } from '../../utils/api';
import axios from 'axios';

export default function ForgotPasswordPage() {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const t = {
    en: {
      title: "Reset your password",
      subtitle: "Enter your email and we'll send you a reset link",
      email: "Email",
      submit: "Send reset link",
      sending: "Sending...",
      successTitle: "Check your email",
      successMessage: "If an account exists for this email, you'll receive a password reset link shortly.",
      backToLogin: "Back to login",
      tryAgain: "Try a different email"
    },
    fr: {
      title: "Réinitialisez votre mot de passe",
      subtitle: "Entrez votre email et nous vous enverrons un lien de réinitialisation",
      email: "Courriel",
      submit: "Envoyer le lien",
      sending: "Envoi en cours...",
      successTitle: "Vérifiez votre email",
      successMessage: "Si un compte existe pour cet email, vous recevrez un lien de réinitialisation sous peu.",
      backToLogin: "Retour à la connexion",
      tryAgain: "Essayer un autre email"
    }
  };

  const text = t[language] || t.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(language === 'fr' ? "Une erreur s'est produite. Réessayez." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-pp-bg">
      <Header />
      
      <main className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="pp-container">
          <div className="max-w-md mx-auto">
            {!sent ? (
              <>
                <div className="text-center mb-10">
                  <div className="w-16 h-16 rounded-2xl bg-pp-navy/10 mx-auto mb-6 flex items-center justify-center">
                    <EnvelopeSimple size={32} className="text-pp-navy" weight="duotone" />
                  </div>
                  <h1 className="pp-heading-2 text-pp-text mb-3" data-testid="forgot-password-title">
                    {text.title}
                  </h1>
                  <p className="text-pp-text-secondary">{text.subtitle}</p>
                </div>

                <div className="pp-card">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="email" className="pp-label mb-2 block">{text.email}</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        className="pp-input"
                        data-testid="forgot-password-email"
                      />
                    </div>

                    {error && (
                      <div className="p-3 rounded-lg bg-pp-error-bg text-pp-error-text text-sm" data-testid="forgot-password-error">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading || !email}
                      className="pp-btn-primary w-full disabled:opacity-50"
                      data-testid="forgot-password-submit"
                    >
                      {loading ? text.sending : text.submit}
                    </button>
                  </form>

                  <div className="mt-6 text-center">
                    <Link 
                      to="/login" 
                      className="inline-flex items-center gap-2 text-pp-navy font-medium hover:underline"
                    >
                      <ArrowLeft size={16} />
                      {text.backToLogin}
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-pp-success-muted mx-auto mb-6 flex items-center justify-center">
                  <CheckCircle size={32} className="text-pp-success" weight="fill" />
                </div>
                <h1 className="pp-heading-2 text-pp-text mb-3" data-testid="forgot-password-success">
                  {text.successTitle}
                </h1>
                <p className="text-pp-text-secondary mb-8 max-w-sm mx-auto">
                  {text.successMessage}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link 
                    to="/login" 
                    className="pp-btn-primary"
                  >
                    {text.backToLogin}
                  </Link>
                  <button 
                    onClick={() => { setSent(false); setEmail(''); }}
                    className="pp-btn-secondary"
                  >
                    {text.tryAgain}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
